import { useState, useEffect } from 'react';
import { Table, Card, Badge, Button, Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import cashSessionService from '../../services/cashSessionService';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const CashSessionHistory = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    estado: '',
    fecha_desde: '',
    fecha_hasta: '',
  });

  const toNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await cashSessionService.getAll(filters);
      
      if (response.success) {
        setSessions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    loadSessions();
  };

  const handleClearFilters = () => {
    setFilters({
      estado: '',
      fecha_desde: '',
      fecha_hasta: '',
    });
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: es });
    } catch (error) {
      return '-';
    }
  };

  const getEstadoBadge = (estado) => {
    return estado === 'abierta' 
      ? <Badge bg="success">Abierta</Badge>
      : <Badge bg="secondary">Cerrada</Badge>;
  };

  const getDiferenciaBadge = (diferencia) => {
    const diff = toNumber(diferencia);
    
    if (diff === 0) {
      return <Badge bg="success">Cuadrado</Badge>;
    }
    if (diff > 0) {
      return <Badge bg="warning">+ ${diff.toFixed(2)}</Badge>;
    }
    return <Badge bg="danger">- ${Math.abs(diff).toFixed(2)}</Badge>;
  };

  // Ganancia neta = lo contado en caja - el fondo inicial
  const getGananciaNeta = (session) => {
    if (!session.monto_real) return '-';
    const neta = toNumber(session.monto_real) - toNumber(session.monto_inicial);
    return `$${neta.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="mb-3">
            <i className="bi bi-clock-history me-2"></i>
            Historial de Sesiones de Caja
          </h2>
        </div>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  name="estado"
                  value={filters.estado}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos</option>
                  <option value="abierta">Abiertas</option>
                  <option value="cerrada">Cerradas</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Desde</Form.Label>
                <Form.Control
                  type="date"
                  name="fecha_desde"
                  value={filters.fecha_desde}
                  onChange={handleFilterChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Hasta</Form.Label>
                <Form.Control
                  type="date"
                  name="fecha_hasta"
                  value={filters.fecha_hasta}
                  onChange={handleFilterChange}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button variant="primary" onClick={handleSearch} className="me-2">
                <i className="bi bi-search me-1"></i>
                Buscar
              </Button>
              <Button variant="outline-secondary" onClick={handleClearFilters}>
                <i className="bi bi-x-circle me-1"></i>
                Limpiar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {sessions.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-1 text-muted"></i>
              <p className="text-muted mt-3">No se encontraron sesiones</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Caja</th>
                  <th>Cajero</th>
                  <th>Apertura</th>
                  <th>Cierre</th>
                  <th>Monto Inicial</th>
                  <th>Ventas</th>
                  <th>Monto Esperado</th>
                  <th>Monto Real</th>
                  <th>Ganancia Neta</th>
                  <th>Diferencia</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(session => (
                  <tr key={session.id}>
                    <td>#{session.id}</td>
                    <td>{session.cash_register?.nombre || 'N/A'}</td>
                    <td>{session.nombre_cajero || '-'}</td>
                    <td>{formatDate(session.fecha_apertura)}</td>
                    <td>{formatDate(session.fecha_cierre)}</td>
                    <td>${toNumber(session.monto_inicial).toFixed(2)}</td>
                    <td>${toNumber(session.monto_ventas_efectivo).toFixed(2)}</td>
                    <td>${toNumber(session.monto_esperado).toFixed(2)}</td>
                    <td>{session.monto_real ? `$${toNumber(session.monto_real).toFixed(2)}` : '-'}</td>
                    <td>{getGananciaNeta(session)}</td>
                    <td>{getDiferenciaBadge(session.diferencia)}</td>
                    <td>{getEstadoBadge(session.estado)}</td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => navigate(`/cash/session/${session.id}`)}
                      >
                        <i className="bi bi-eye me-1"></i>
                        Ver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CashSessionHistory;